import { z } from 'zod';
import { TRIAL_VISIT_TYPES } from '../trials/trials.schema.js';
// ---------------------------------------------------------------------------
// Template visits (#41) — spec: spec/features/patients-pathways/
// template-visits.feature (frozen 2026-10-28, REVISED the same day per the
// #43/#42 decisions). Non-protocol preset visits (UNS / SCREENING FAILURE /
// OUT) added straight onto the participant's schedule — never into the
// protocol catalog (REQ-PAT-024 prompt-wins; catalog stays the single
// protocol truth). #43 repeatability is CATEGORICAL: category UNS repeats
// without limit (each add creates the next unplanned visit), category OUT
// (SCREENING FAILURE, OUT rows) is one-time per enrollment (patient×trial).
// Rules key on the category `code`, never on the configured `name`
// (GO 3: names are configuration data).
// ---------------------------------------------------------------------------
/**
 * Preset category code — the dialog's „kod" column (screenshot #41):
 * UNS = the unplanned site visit (repeatable — #43 supersedes the UNS1…UNS5
 * rows with ONE preset added as many times as needed), OUT = last-contact
 * phone visits (SCREENING FAILURE, OUT; one-time per enrollment). The code
 * is a CATEGORY, not a row identity; rows are identified by `name` (unique
 * within the configured set).
 */
export const TEMPLATE_VISIT_CODES = {
    UNS: 'UNS',
    OUT: 'OUT',
};
export const TemplateVisitCodeSchema = z.enum(Object.values(TEMPLATE_VISIT_CODES));
/**
 * Width cap for a preset name; matches the future `patient_visits` preset
 * column (DDL atom) and the `screeningNumber` cap convention.
 */
export const TEMPLATE_VISIT_PRESET_NAME_MAX = 64;
/**
 * One dialog row of the „Wizyty szablonowe" preset set. The set itself is
 * DATA/CONFIGURATION (GO conclusion 3): the #43 decision (5×UNS vs 1×UNS)
 * changes the configured rows, not this contract — nothing here hardcodes
 * the UNS count or names beyond the category codes.
 */
export const TemplateVisitPresetDtoSchema = z.object({
    /** Row identity within the configured set (e.g. „UNS1", „SCREENING FAILURE", „OUT"). */
    name: z.string().min(1).max(TEMPLATE_VISIT_PRESET_NAME_MAX),
    code: TemplateVisitCodeSchema,
    /**
     * Contact channel from the preset configuration (UNS → `S` site,
     * SCREENING FAILURE / OUT → `P` phone). Full legacy S/P/H/I enum — the
     * channel is configuration data, the contract does not pin the mapping.
     */
    type: z.enum(Object.values(TRIAL_VISIT_TYPES)),
    /** Row description, e.g. „Wizyta nieplanowa" / „Niepowodzenie screeningu". */
    description: z.string().min(1),
    /**
     * #43 (decision 2026-10-28, supersedes GO conclusion 1): whether the
     * preset's category repeats. `true` (UNS) — the row can be added again
     * and again, each add creating the next visit; its checkbox stays ACTIVE
     * and `alreadyAdded` is purely informational ("added at least once").
     * `false` (OUT category) — one-time per enrollment; an already-added row
     * renders with an INACTIVE checkbox and a duplicate add is rejected with
     * 409 TEMPLATE_VISIT_DUPLICATE. Carried from the preset CONFIGURATION so
     * the UI never hardcodes category semantics.
     */
    repeatable: z.boolean(),
    /** At least one visit of this preset exists on the enrollment's schedule. */
    alreadyAdded: z.boolean(),
});
/**
 * The dialog state read (`GET /patients/:id/enrollments/:enrollmentId/
 * schedule/template-visits`, route lands with the API atom): the configured
 * preset set with per-enrollment `alreadyAdded` / `repeatable` flags. No
 * pagination — the set is bounded by configuration (single digits today),
 * rendered whole.
 */
export const TemplateVisitsDialogDtoSchema = z.object({
    items: z.array(TemplateVisitPresetDtoSchema),
});
/**
 * `POST …/schedule/template-visits` (route lands with the API atom): the
 * whole „DODAJ WIZYTY" bulk in ONE all-or-nothing action — a duplicate of a
 * ONE-TIME preset (category OUT) anywhere in the list rejects the ENTIRE
 * operation with 409 `TEMPLATE_VISIT_DUPLICATE` (T5 pattern from #39 bulk
 * doors; spec B3/B4 as revised by #43 — a repeatable UNS row is NEVER a
 * duplicate, adding it again simply creates the next visit). Rows are
 * identified by configured preset `name`s. Non-empty list: the `min(1)` is
 * the early 400 gate for an empty selection (the service repeats it as a
 * belt-and-suspenders guard, `AssignScheduleVisitsRequest` pattern).
 *
 * Response: the created visits in REQUEST order in the `items` envelope —
 * `ScheduleVisitsDtoSchema` (schedule-log.schema.ts). Preset rows carry no
 * catalog position: `PatientVisitDto.trialVisitId` is nullable and rows
 * carry `presetName` (atoms 3a/3b-1). The category carrier `preset_code`
 * stays DB/service-internal — the dialog reads `repeatable` from this
 * contract's preset configuration instead (decision 2026-10-28).
 */
export const AddTemplateVisitsRequestSchema = z.object({
    presetNames: z
        .array(z.string().trim().min(1).max(TEMPLATE_VISIT_PRESET_NAME_MAX))
        .min(1),
});
//# sourceMappingURL=template-visits.schema.js.map